import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';

// utils
import axios from '../utils/axios';

//
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType, JWTContextType } from './types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  SIGNUP = 'SIGNUP',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.SIGNUP]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.SIGNUP) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = await await localStorage.getItem('accessToken');

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        let user: AuthUserType = null;
        try {
          const response = await axios.get(`user/client-validate-user`);
          user = response.data;
        } catch {
          // Admin SSO: SuperAdmin token may not pass client-validate-user; use stored user.
          const storedUser = localStorage.getItem('user') || localStorage.getItem('userdetails');
          if (storedUser) {
            try {
              user = JSON.parse(storedUser);
            } catch {
              user = storedUser as unknown as AuthUserType;
            }
          }
        }

        if (!user) {
          const storedUser = localStorage.getItem('user') || localStorage.getItem('userdetails');
          if (storedUser) {
            try {
              user = JSON.parse(storedUser);
            } catch {
              user = storedUser as unknown as AuthUserType;
            }
          }
        }

        if (user) {
          dispatch({
            type: Types.INITIAL,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          return;
        }

        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const response = await axios.post('/user/client-login', {
      email,
      password,
    });

    const { accessToken, user, uploadSettings } = response.data;
console.log('User123456', user); 

if (user.role === "Company") {

  localStorage.setItem('company_id', user.companyID);
}

    if (accessToken) {
      await localStorage.setItem('accessToken', accessToken);
      await localStorage.setItem('userdetails', JSON.stringify(user));
      await localStorage.setItem('userRole', user.role);
      await localStorage.setItem('userId', user._id);
      await localStorage.setItem('companyID', user.companyID);
      console.log('companyIDcompanyIDcompanyID', user.companyID);
      await localStorage.setItem('uploadSettings', JSON.stringify(uploadSettings));
      axios.defaults.headers.Authorization = `Bearer ${accessToken}`;
    }

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
  }, []);
  const signUp = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      phoneNumber: string,
      jobTitle: string,
      companyID: string,
      role: string,
      admin: boolean
    ) => {
      const response = await axios.post('/user/client-signup', {
        email,
        password,
        firstName,
        lastName,
        phoneNumber,
        jobTitle,
        companyID,
        role,
        admin,
      });

      const { accessToken, user, uploadSettings } = response.data;
      console.log("user", user);
      const resolvedCompanyID = user?.companyID || companyID;
      if (user.role === 'Company') {
        localStorage.setItem('company_id', resolvedCompanyID);
      }
      if (accessToken) {
        const userWithCompanyId = user?.companyID ? user : { ...user, companyID: resolvedCompanyID };
        await localStorage.setItem('accessToken', accessToken);
        await localStorage.setItem('userdetails', JSON.stringify(userWithCompanyId));
        await localStorage.setItem('uploadSettings', JSON.stringify(uploadSettings));
        await localStorage.setItem('userRole', user.role);
        await localStorage.setItem('userId', user._id);
        await localStorage.setItem('companyID', resolvedCompanyID);
        axios.defaults.headers.Authorization = `Bearer ${accessToken}`;
      }
      dispatch({
        type: Types.SIGNUP,
        payload: {
          user: user?.companyID ? user : { ...user, companyID: resolvedCompanyID },
        },
      });
    },
    []
  );

  // REGISTER
  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      mobileNumber: string,
      companyId: string
    ) => {
      const response = await axios.post('/company-user/register', {
        email,
        password,
        firstName,
        lastName,
        mobileNumber,
        companyId,
        role: 'admin',
      });
      const { accessToken, user } = response.data;

      if (accessToken) {
        sessionStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.Authorization = `Bearer ${accessToken}`;
      }

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      // @ts-ignore
      accessToken: state.accessToken,
      method: 'jwt',
      login,
      loginWithGoogle: () => {},
      loginWithGithub: () => {},
      loginWithTwitter: () => {},
      register,
      signUp,
      logout,
      initialize,
    }),
    // @ts-ignore
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.accessToken,
      login,
      signUp,
      logout,
      register,
      initialize,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
